import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Table,
  Button,
  Modal,
  Form,
  ProgressBar,
} from "react-bootstrap";
import { ref, get, set } from "firebase/database";
import { database } from "../firebaseConfig";
import { getAuth } from "firebase/auth";
import { Worker, Viewer } from "@react-pdf-viewer/core";
import "@react-pdf-viewer/core/lib/styles/index.css";

const ControlPage = () => {
  const [userData, setUserData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [userNames, setUserNames] = useState({});
  const [evaluatedFile, setEvaluatedFile] = useState(null);
  const [detailsReportFile, setDetailsReportFile] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showReferenceLinkModal, setShowReferenceLinkModal] = useState(false);
  const [tempReferenceLink, setTempReferenceLink] = useState("");
  const [videoLink, setVideoLink] = useState("");
  const auth = getAuth();
  const [showModal, setShowModal] = useState(false);
  const [selectedFileLink, setSelectedFileLink] = useState(null);

  const handleShow = () => setShowModal(true);
  const handleClose = () => setShowModal(false);
  

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const snapshot = await get(ref(database, "shared_data/"));
        const data = snapshot.val();
        const dataList = [];

        for (let id in data) {
          if (data[id].answers) {
            for (let answerId in data[id].answers) {
              dataList.push({
                sharedDataId: id,
                answerId,
                email: data[id].email || "N/A",
                uploadedAt: data[id].uploadedAt || null,
                ...data[id],
                ...data[id].answers[answerId],
              });
            }
          }
        }
        setUserData(dataList);
        setFilteredData(dataList); // Initialize filtered data

        const usersSnapshot = await get(ref(database, "users/"));
        const usersData = usersSnapshot.val();
        const namesMap = {};
        for (let userId in usersData) {
          namesMap[usersData[userId].email] = usersData[userId].name || "N/A";
        }
        setUserNames(namesMap);
      } catch (error) {
        console.error("Error fetching user data from Firebase:", error);
      }
    };

    fetchUserData();
  }, []);

  // Handle search input change
  const handleSearchChange = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);

    // Filter data based on title
    const filtered = userData.filter(
      (user) => user.title && user.title.toLowerCase().includes(query)
    );
    setFilteredData(filtered);
  };

  const handleShowUploadModal = (user) => {
    setSelectedUser(user);
    setShowUploadModal(true);
    setErrorMessage("");
    setSuccessMessage("");
  };

  const handleCloseUploadModal = () => {
    setShowUploadModal(false);
    setSelectedUser(null);
    setEvaluatedFile(null);
    setDetailsReportFile(null);
    setUploading(false);
    setProgress(0);
    setErrorMessage("");
    setSuccessMessage("");
  };

  const handleShowReferenceLinkModal = (user) => {
    setSelectedUser(user);
    setTempReferenceLink("");
    setShowReferenceLinkModal(true);
    setErrorMessage("");
    setSuccessMessage("");
  };

  const handleCloseReferenceLinkModal = () => {
    setShowReferenceLinkModal(false);
    setSelectedUser(null);
    setTempReferenceLink("");
  };

  const handleFileChange = (e, setFile) => {
    const file = e.target.files[0];
    setFile(file);
  };
  const toBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result.split(",")[1]);
      reader.onerror = (error) => reject(error);
    });
  };

  const uploadReferenceLink = async () => {
    if (!tempReferenceLink || !selectedUser) {
      setErrorMessage(
        "Please enter reference text and ensure a user is selected."
      );
      return;
    }

    try {
      // Store the multiline reference text
      await set(
        ref(
          database,
          `shared_data/${selectedUser.sharedDataId}/answers/${selectedUser.answerId}/reference_text`
        ),
        {
          text: tempReferenceLink,
          uploadedAt: new Date().toISOString(),
          evaluator: auth.currentUser ? auth.currentUser.email : "Unknown",
        }
      );

      setSuccessMessage("Reference text uploaded successfully!");
      setTimeout(() => handleCloseReferenceLinkModal(), 3000);
    } catch (error) {
      setErrorMessage(error.message);
    }
  };

  const handleEditReferenceText = () => {
    if (!tempReferenceLink || !selectedUser) {
      setErrorMessage(
        "Please enter reference text to update and ensure a user is selected."
      );
      return;
    }

    try {
      // Update the existing reference text
      set(
        ref(
          database,
          `shared_data/${selectedUser.sharedDataId}/answers/${selectedUser.answerId}/reference_text`
        ),
        {
          text: tempReferenceLink,
          uploadedAt: new Date().toISOString(),
          evaluator: auth.currentUser ? auth.currentUser.email : "Unknown",
        }
      );
      setSuccessMessage("Reference text updated successfully!");
      setTimeout(() => handleCloseReferenceLinkModal(), 3000);
    } catch (error) {
      setErrorMessage(error.message);
    }
  };

  const uploadFiles = async () => {
    if ((!evaluatedFile && !detailsReportFile && !videoLink) || !selectedUser) {
      setErrorMessage(
        "Please select files or enter a video link to upload and ensure a user is selected."
      );
      return;
    }

    setUploading(true);
    setProgress(0);

    const githubRepos = [
      "E1",
      "E2",
      "E3",
      "E4",
      "E5",
      "E6",
      "E7",
      "E8",
      "E9",
      "E10",
    ];

    const uploadToGitHub = async (file, folder) => {
      const fileData = await toBase64(file);
      const fileName = `${selectedUser.email}_${Date.now()}_${file.name}`;

      // Loop through repositories to try each
      for (let i = 0; i < githubRepos.length; i++) {
        const repo = githubRepos[i];
        try {
          const response = await fetch(
            `https://api.github.com/repos/pragyeshias/${repo}/contents/${folder}/${fileName}`,
            {
              method: "PUT",
              headers: {
                Authorization: `token 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`,
                Accept: "application/vnd.github.v3+json",
              },
              body: JSON.stringify({
                message: `Upload ${folder}: ${fileName}`,
                content: fileData,
              }),
            }
          );

          if (!response.ok) {
            throw new Error(`Error uploading ${file.name} to ${repo}`);
          }

          return `https://raw.githubusercontent.com/pragyeshias/${repo}/main/${folder}/${fileName}`;
        } catch (error) {
          if (i === githubRepos.length - 1) {
            throw new Error(`All repositories failed for ${file.name}`);
          }
          // Proceed to the next repo if the current one fails
        }
      }
    };

    try {
      const totalSteps =
        [evaluatedFile, detailsReportFile, videoLink].filter(Boolean).length +
        1;
      let currentStep = 0;

      let evaluatedFileLink = "";
      if (evaluatedFile) {
        evaluatedFileLink = await uploadToGitHub(
          evaluatedFile,
          "evaluated_answers"
        );
        setProgress((++currentStep / totalSteps) * 100);
      }

      let detailsReportFileLink = "";
      if (detailsReportFile) {
        detailsReportFileLink = await uploadToGitHub(
          detailsReportFile,
          "details_reports"
        );
        setProgress((++currentStep / totalSteps) * 100);
      }

      await set(
        ref(
          database,
          `shared_data/${selectedUser.sharedDataId}/answers/${selectedUser.answerId}/evaluated_answer`
        ),
        {
          fileLink: evaluatedFileLink,
          uploadedAt: new Date().toISOString(),
          evaluator: auth.currentUser ? auth.currentUser.email : "Unknown",
        }
      );

      await set(
        ref(
          database,
          `shared_data/${selectedUser.sharedDataId}/answers/${selectedUser.answerId}/details_report`
        ),
        {
          fileLink: detailsReportFileLink,
          uploadedAt: new Date().toISOString(),
          evaluator: auth.currentUser ? auth.currentUser.email : "Unknown",
        }
      );

      if (videoLink) {
        await set(
          ref(
            database,
            `shared_data/${selectedUser.sharedDataId}/answers/${selectedUser.answerId}/video_link`
          ),
          {
            link: videoLink,
            uploadedAt: new Date().toISOString(),
            evaluator: auth.currentUser ? auth.currentUser.email : "Unknown",
          }
        );
      }

      setProgress(100);
      setSuccessMessage("Files and video link uploaded successfully!");
      setUploading(false);
      setTimeout(() => handleCloseUploadModal(), 3000);
    } catch (error) {
      setErrorMessage(error.message);
      setUploading(false);
    }
  };

  return (
    <div className="control-page">
      <Container style={{ marginTop: "80px" }}>
        <h1 className="mt-4 text-center text-primary">Evaluate Answers</h1>
        <p className="text-center">
          Manage and review all uploaded answers and evaluate them.
        </p>

        <Row className="mb-3">
          <Col md={6} className="mx-auto">
            <Form.Control
              type="text"
              placeholder="Search by title..."
              value={searchQuery}
              onChange={handleSearchChange}
            />
          </Col>
        </Row>

        <Row>
          <Col>
            <Table striped bordered hover responsive className="shadow-sm">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Title</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Uploaded At</th>
                  <th>Question File</th>
                  <th>Answer File</th>
                  <th>Evaluated File</th>
                  <th>Details Report</th>
                  <th>Video Link</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.map((user, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{user.title || "N/A"}</td>
                    <td>{userNames[user.email] || "N/A"}</td>
                    <td>{user.email}</td>
                    {/* <td>{user.sharedDataId}</td> */}
                    <td>
                      {user.uploadedAt
                        ? new Date(user.uploadedAt).toLocaleString()
                        : "N/A"}
                    </td>
                    <td>
                      <a
                        href={user.questionPaperLink}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Download Question
                      </a>
                    </td>
                    <td>
                      <Button
                        variant="primary"
                        onClick={() => {
                          setSelectedFileLink(user.fileLink);
                          handleShow();
                        }}
                      >
                        View Answer
                      </Button>
                    </td>
                    <td>
                      {user.evaluated_answer ? (
                        <a
                          href={user.evaluated_answer.fileLink}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Download Evaluated Answer
                        </a>
                      ) : (
                        "Not Uploaded"
                      )}
                    </td>
                    <td>
                      {user.details_report ? (
                        <a
                          href={user.details_report.fileLink}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Download Details Report
                        </a>
                      ) : (
                        "Not Uploaded"
                      )}
                    </td>
                    <td>
                      {user.video_link ? (
                        <a
                          href={user.video_link.link}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Watch Video
                        </a>
                      ) : (
                        "Not Uploaded"
                      )}
                    </td>

                    <td>
                      {!user.evaluated_answer && (
                        <Button
                          variant="primary"
                          onClick={() => handleShowUploadModal(user)}
                        >
                          Upload Evaluated Answer & Report
                        </Button>
                      )}
                      <Button
                        variant="secondary"
                        className="mt-2"
                        onClick={() => handleShowReferenceLinkModal(user)}
                      >
                        {user.reference_text
                          ? "View Reference Text"
                          : "Upload Reference Text"}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Col>
        </Row>
      </Container>

      <Modal
        show={showModal}
        onHide={handleClose}
        size="lg"
        centered
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>Document Viewer</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedFileLink ? (
            <div style={{ height: 550 }}>
              <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
                <Viewer fileUrl={selectedFileLink} />
              </Worker>
            </div>
          ) : (
            <p className="text-center text-muted">
              No document available to display.
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Close
          </Button>
          {selectedFileLink && (
            <>
              <a
                href={`https://docs.google.com/viewer?url=${encodeURIComponent(
                  selectedFileLink
                )}&embedded=true`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-success"
              >
                View in New Tab
              </a>
              <a
                href={selectedFileLink}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-success"
              >
                Download
              </a>
            </>
          )}
        </Modal.Footer>
      </Modal>

      <Modal
        show={showUploadModal}
        onHide={handleCloseUploadModal}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Upload Evaluated Answer, Report & Video Link
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-4">
            <Form.Label>Evaluated Answer</Form.Label>
            <Form.Control
              type="file"
              onChange={(e) => handleFileChange(e, setEvaluatedFile)}
            />
          </Form.Group>
          <Form.Group className="mb-4">
            <Form.Label>Details Report</Form.Label>
            <Form.Control
              type="file"
              onChange={(e) => handleFileChange(e, setDetailsReportFile)}
            />
          </Form.Group>
          <Form.Group className="mb-4">
            <Form.Label>Video Link</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter video link"
              value={videoLink}
              onChange={(e) => setVideoLink(e.target.value)}
            />
          </Form.Group>
          {uploading && (
            <ProgressBar
              animated
              now={progress}
              label={`${Math.round(progress)}%`}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleCloseUploadModal}
            disabled={uploading}
          >
            Close
          </Button>
          <Button variant="primary" onClick={uploadFiles} disabled={uploading}>
            Upload
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal
        show={showReferenceLinkModal}
        onHide={handleCloseReferenceLinkModal}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedUser?.reference_text
              ? "Edit Reference Text"
              : "Upload Reference Text"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {errorMessage} {successMessage}
          <Form.Group className="mb-4">
            <Form.Label>Reference Text (up to 500 lines)</Form.Label>
            <Form.Control
              as="textarea"
              rows={10}
              placeholder="Enter or edit reference text here..."
              value={
                tempReferenceLink || selectedUser?.reference_text?.text || ""
              }
              onChange={(e) => setTempReferenceLink(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseReferenceLinkModal}>
            Close
          </Button>
          {selectedUser?.reference_text ? (
            <Button variant="primary" onClick={handleEditReferenceText}>
              Update
            </Button>
          ) : (
            <Button variant="primary" onClick={uploadReferenceLink}>
              Upload
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ControlPage;
